/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { makeT } from "app/client/lib/localization";
import { theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { unstyledButton } from "app/client/ui2018/unstyled";

import { dom, DomElementArg, Observable, styled } from "grainjs";

const t = makeT(`AddNewButton`);

export function addNewButton(
  {
    isOpen,
    isDisabled = false,
  }: {
    isOpen: Observable<boolean> | boolean,
    isDisabled?: boolean
  },
  ...args: DomElementArg[]
) {
  return cssAddNewButton(
    cssAddNewButton.cls("-open", isOpen),
    cssAddNewButton.cls("-disabled", isDisabled),
    // Setting spacing as flex items allows them to shrink faster when there isn't enough space.
    cssLeftMargin(),
    cssAddText(t("Add new")),
    dom("div", { style: "flex: 1 1 16px" }),
    cssPlusButton(
      cssPlusButton.cls("-disabled", isDisabled),
      cssPlusIcon("Plus"),
    ),
    dom("div", { style: "flex: 0 1 16px" }),
    ...args,
  );
}

export const cssAddNewButton = styled(unstyledButton, `
  display: flex;
  align-items: center;
  margin: 16px 0px 16px 0px;
  height: 42px;
  color: ${theme.controlPrimaryBg};
  background-color: color-mix(in srgb, ${theme.controlPrimaryBg} 12%, transparent);
  border: 1px solid color-mix(in srgb, ${theme.controlPrimaryBg} 25%, transparent);
  border-radius: 12px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  cursor: pointer;
  text-align: left;
  font-size: ${vars.bigControlFontSize};
  font-weight: 600;
  letter-spacing: -0.01em;
  overflow: hidden;

  /* make sure keyboard highlight is not glued to the button,
  as it is the same color as the button background */
  outline-offset: 2px;

  --circle-color: color-mix(in srgb, ${theme.controlPrimaryBg} 18%, transparent);

  &:hover, &.weasel-popup-open {
    background-color: color-mix(in srgb, ${theme.controlPrimaryBg} 18%, transparent);
    border-color: color-mix(in srgb, ${theme.controlPrimaryBg} 35%, transparent);
    --circle-color: color-mix(in srgb, ${theme.controlPrimaryBg} 24%, transparent);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  &:active {
    transform: scale(0.985);
  }
  &-open {
    margin: 16px 14px 16px 14px;
    background-color: color-mix(in srgb, ${theme.controlPrimaryBg} 12%, transparent);
    border: 1px solid color-mix(in srgb, ${theme.controlPrimaryBg} 25%, transparent);
    --circle-color: color-mix(in srgb, ${theme.controlPrimaryBg} 18%, transparent);
  }
  &-open:hover, &-open.weasel-popup-open {
    background-color: color-mix(in srgb, ${theme.controlPrimaryBg} 18%, transparent);
    border-color: color-mix(in srgb, ${theme.controlPrimaryBg} 35%, transparent);
    --circle-color: color-mix(in srgb, ${theme.controlPrimaryBg} 24%, transparent);
  }

  &-disabled, &-disabled:hover {
    color: ${theme.controlDisabledFg};
    background-color: ${theme.controlDisabledBg};
    box-shadow: none;
    transform: none;
    cursor: default;
  }
`);
const cssLeftMargin = styled("div", `
  flex: 0 1 24px;
  display: none;
  .${cssAddNewButton.className}-open & {
    display: block;
  }
`);
const cssAddText = styled("div", `
  color: ${theme.controlPrimaryBg};
  flex: 0 0.5 content;
  white-space: nowrap;
  min-width: 0px;
  display: none;
  .${cssAddNewButton.className}-open & {
    display: block;
  }
`);
const cssPlusButton = styled("div", `
  flex: none;
  height: 28px;
  width: 28px;
  border-radius: 14px;
  background-color: var(--circle-color);
  text-align: center;
  &-disabled {
    background-color: ${theme.controlDisabledBg};
  }
`);
const cssPlusIcon = styled(icon, `
  background-color: ${theme.controlPrimaryBg};
  margin-top: 6px;
`);
